import { useFormApi } from 'informed';
import { ApplicationData } from '../../../../types/TymbeApi';
import { AttendanceResolution } from '@tymbe/schema/enums';
import UnapprovedAttendanceRow from './UnapprovedAttendanceRow';
import { BadgePill } from '@tymbe/ty-components/indicators';
import { Fragment, useCallback } from 'react';

type ShiftAttendanceProps = {
  applications: ApplicationData[];
  startDate: string;
  endDate: string;
  onSubmit: (values: AttendanceScope[]) => Promise<unknown> | void;
  isLoading?: boolean;
  disabled?: boolean;
};

export type AttendanceScope = {
  id: number;
  confirmed_time: number;
  break: number;
  start: number;
  end: number;
  rating: number;
  selected: boolean;
  resolution: AttendanceResolution;
  block: FormValues;
};

export type FormValues = {
  applications: AttendanceScope[];
};

const ShiftUnapprovedAttendance = ({
  applications,
  startDate,
  endDate,
  onSubmit,
  disabled = false,
  isLoading = false,
}: ShiftAttendanceProps) => {
  const formApi = useFormApi();

  const submitSingle = useCallback(
    async (key: number) => {
      const values: AttendanceScope = formApi?.getValue(`applications.${key}`);
      if (!values) return;
      try {
        await onSubmit([values]);
      } catch {
        /* can be safely ignored */
      }
    },
    [formApi, onSubmit]
  );

  return (
    <div className="flex flex-col gap-4 md:grid md:grid-rows-[60px,1fr] md:overflow-x-auto md:min-w-[1000px]">
      <div className="text-body-2-xl font-normal flex flex-col md:grid md:grid-cols-[250px_110px_110px_160px_minmax(300px,1fr)] md:grid-rows-1 md:row-start-1 md:row-span-1">
        <div className="flex items-center gap-2 md:col-start-1">
          <span className="text-secondary-600 font-semibold">Docházka k potvrzení</span>
          <BadgePill variant="info">{applications.length}</BadgePill>
        </div>
        {applications.length !== 0 && (
          <>
            <div className="hidden md:col-start-2 md:flex">
              <span className="my-auto">Odpracováno</span>
            </div>
            <div className="hidden md:col-start-3 md:flex">
              <span className="my-auto">Přestávka</span>
            </div>
            <div className="hidden md:col-start-4 md:flex">
              <span className="my-auto">Příchod a odchod</span>
            </div>
          </>
        )}
      </div>

      {applications.length ? (
        <div className="md:gap-0 gap-4 grid grid-cols-1 md:rounded-2xl md:grid-rows-1 md:row-start-2 md:row-span-1">
          {applications.map((application, index) => (
            <Fragment key={application.id}>
              {index > 0 && <hr className="border-secondary-transparent-10 md:col-span-full" />}
              <div
                className={`flex flex-col gap-4 md:grid md:col-span-full md:grid-cols-subgrid border-secondary-transparent-10 ${
                  index === 0
                    ? applications.length === 1
                      ? 'border rounded-2xl'
                      : 'border-t border-l border-r rounded-t-2xl'
                    : index === applications.length - 1
                    ? 'border-b border-l border-r rounded-b-2xl'
                    : 'border-l border-r'
                }`}
              >
                <UnapprovedAttendanceRow
                  key={application.id}
                  application={application}
                  submitSingle={submitSingle}
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ShiftUnapprovedAttendance;
